// Top of the dashboard: greeting and actions on a navy banner, with the
// featured slides (managed on the Admin page) framed on the right; the
// catalog numbers sit just below.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, ChevronRight, Layers, Lightbulb, Play, Plus, type LucideIcon } from "lucide-react";
import type { DashboardCounts } from "../../hooks/useDashboardData";
import type { FeaturedSlide } from "../../data/featuredContent";
import { playVideo, posterFor, VideoLightbox, type Embed } from "../VideoPlayer";
import { greeting } from "./format";

const ROTATE_MS = 8000;

function SlideLink({ link }: { link: NonNullable<FeaturedSlide["link"]> }) {
  const inner = (
    <>
      {link.label} <ArrowUpRight size={13} aria-hidden />
    </>
  );
  return link.url.startsWith("/") ? (
    <Link className="db-hero-caption-link" to={link.url}>
      {inner}
    </Link>
  ) : (
    <a className="db-hero-caption-link" href={link.url} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  );
}

/** The featured slides: a framed screen with a caption, rotating when there
 *  are several. Also used as the live preview in the banner editor. */
export function Featured({ slides }: { slides: FeaturedSlide[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [playing, setPlaying] = useState<Embed | null>(null);

  useEffect(() => {
    if (slides.length < 2 || paused || playing) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => window.clearInterval(t);
  }, [slides.length, paused, playing]);

  const slide = slides[index % slides.length];
  if (!slide) return null;
  const poster = slide.textOnly ? undefined : posterFor(slide.videoUrl, slide.image);
  const watch = () => {
    if (!slide.videoUrl) return;
    if (slide.player) setPlaying(slide.player);
    else if (slide.player === null) window.open(slide.videoUrl, "_blank", "noopener,noreferrer");
    else setPlaying(playVideo(slide.videoUrl));
  };

  return (
    <div
      className="db-hero-feature"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <button
        type="button"
        className={`db-hero-screen${slide.textOnly ? " db-hero-screen--text" : ""}`}
        onClick={watch}
        disabled={!slide.videoUrl}
        aria-label={slide.videoUrl ? `Play ${slide.title}` : slide.title}
      >
        {poster ? <img src={poster} alt="" /> : null}
        {slide.textOnly ? (
          <span className="db-hero-note">
            {slide.tag ? <span className="db-hero-note-tag">{slide.tag}</span> : null}
            <span className="db-hero-note-title">{slide.title}</span>
            {slide.description ? <span className="db-hero-note-text">{slide.description}</span> : null}
          </span>
        ) : null}
        {slide.videoUrl ? (
          <span className="db-play" aria-hidden>
            <Play size={18} fill="currentColor" />
          </span>
        ) : null}
      </button>
      <div className="db-hero-caption">
        <span className="db-hero-tag">Featured{slide.tag ? ` · ${slide.tag}` : ""}{slide.meta ? ` · ${slide.meta}` : ""}</span>
        <span className="db-hero-caption-title">{slide.title}</span>
        {slide.link ? <SlideLink link={slide.link} /> : null}
        {slides.length > 1 ? (
          <span className="db-dots">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className={`db-dot${s === slide ? " is-active" : ""}`}
                onClick={() => setIndex(i)}
                aria-label={`Show ${s.title}`}
                aria-current={s === slide}
              />
            ))}
          </span>
        ) : null}
      </div>
      {playing ? <VideoLightbox title={slide.title} embed={playing} onClose={() => setPlaying(null)} /> : null}
    </div>
  );
}

function Stat({
  to,
  icon: Icon,
  tone,
  value,
  label,
  note,
  loading,
}: {
  to: string;
  icon: LucideIcon;
  tone: string;
  value: number | undefined;
  label: string;
  note?: string;
  loading: boolean;
}) {
  return (
    <Link className={`db-stat db-stat--${tone}`} to={to}>
      <span className="db-stat-icon" aria-hidden>
        <Icon size={16} />
      </span>
      <span className="db-stat-value">{loading ? "–" : (value ?? 0).toLocaleString()}</span>
      <span className="db-stat-label">{label}</span>
      {note ? <span className="db-stat-note">{note}</span> : null}
      <ChevronRight className="db-stat-arrow" size={15} aria-hidden />
    </Link>
  );
}

export function DashHero({
  firstName,
  slides,
  slidesLoading,
  counts,
  loading,
}: {
  firstName?: string;
  slides: FeaturedSlide[];
  slidesLoading?: boolean;
  counts: DashboardCounts | null;
  loading: boolean;
}) {
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <>
      <section className={`db-hero${slides.length || slidesLoading ? "" : " db-hero--solo"}`} aria-label="Welcome">
        <div className="db-hero-text">
          <p className="db-hero-date">{today}</p>
          <h1 className="db-hero-hello">
            {greeting()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="db-hero-lede">What's new in AI, what it means for homebuilding, and what's on hand at Brookfield.</p>
          <div className="db-hero-actions">
            <Link className="db-hero-btn db-hero-btn--primary" to="/initiatives?new=1">
              <Plus size={16} aria-hidden /> Submit an AI idea
            </Link>
            <Link className="db-hero-btn" to="/library">
              <BookOpen size={16} aria-hidden /> Browse the library
            </Link>
          </div>
        </div>
        {slidesLoading ? (
          <div className="db-hero-feature" aria-hidden>
            <span className="db-hero-screen db-hero-screen--loading" />
          </div>
        ) : slides.length ? (
          <Featured slides={slides} />
        ) : null}
      </section>

      <nav className="db-stats" aria-label="The catalog">
        <Stat
          to="/library"
          icon={BookOpen}
          tone="library"
          value={counts?.library}
          label="AI tools & prompts"
          note={counts?.newThisMonth ? `${counts.newThisMonth} new this month` : undefined}
          loading={loading}
        />
        <Stat to="/initiatives" icon={Lightbulb} tone="initiative" value={counts?.initiatives} label="ideas from our teams" loading={loading} />
        <Stat to="/platforms" icon={Layers} tone="platform" value={counts?.platforms} label="approved AI platforms" loading={loading} />
      </nav>
    </>
  );
}
